import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Cake } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * Self-service dialog for any user to set their own birthday and joining date.
 * Backed by PUT /api/users/me/profile-dates.
 */
export const MyProfileDatesDialog = ({ open, onOpenChange, initialDOB = '', initialJoin = '', onSaved }) => {
  const [dob, setDob] = useState('');
  const [join, setJoin] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDob(initialDOB || '');
      setJoin(initialJoin || '');
    }
  }, [open, initialDOB, initialJoin]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {};
      payload.date_of_birth = dob || '';
      payload.joining_date = join || '';
      await api.put('/users/me/profile-dates', payload);
      toast.success('Profile dates saved');
      onOpenChange(false);
      if (onSaved) onSaved({ date_of_birth: dob || null, joining_date: join || null });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="my-profile-dates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cake className="h-5 w-5 text-pink-500" />
            My birthday & work anniversary
          </DialogTitle>
          <DialogDescription>
            Help your team celebrate with you. These dates are only used to show wishes — the year of birth is not shown anywhere.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Date of birth</Label>
            <Input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              data-testid="my-dob-input"
            />
          </div>
          <div>
            <Label className="text-sm">Work joining date</Label>
            <Input
              type="date"
              value={join}
              onChange={(e) => setJoin(e.target.value)}
              data-testid="my-join-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-pink-600 hover:bg-pink-700" data-testid="my-profile-dates-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
